/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: AppearancePage.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Comprehensive appearance settings page with theme customization,
    color management, and UI preferences.

 🔧 Notes:
    - Supports light/dark/system theme modes
    - Custom color picker with real-time preview
    - Notification and display settings
    - Animation and accessibility options
    - Auto-save functionality for real-time updates

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React from 'react';
import { Palette, Monitor, MessageSquare, RotateCcw, LayoutGrid, Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../hooks/useNotifications';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import FixedActionBar from '../../components/ui/FixedActionBar';
import { useFixedActionBar } from '../../hooks/useFixedActionBar';
import { ModuleFormHeader } from '../../components/layouts/ModuleFormHeader';
import { useAppearanceForm, useAppearanceSubmit } from './hooks';
import { ThemeTab, ColorsTab, TooltipsTab, DashboardTab, NotificationsTab } from './components';
import { colorPresets } from './utils';
import type { ColorPreset } from './types';

export default function AppearancePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { notifications, removeNotification, showSuccess, showError, showInfo } = useNotifications();

  // Hook to handle fixed action bar
  useFixedActionBar(true);

  // Form management
  const { register, handleSubmit, watch, setValue, watchedValues, isDirty } = useAppearanceForm();

  // Submit handlers - pass notification functions and isDirty state
  const { handleSubmit: onSubmit, handleReset, isLoading } = useAppearanceSubmit({ showSuccess, showError, showInfo, isDirty });

  const applyColorPreset = (preset: ColorPreset) => {
    setValue('primaryColor', preset.primary);
    setValue('secondaryColor', preset.secondary);
  };

  const onReset = () => {
    handleReset(setValue, watchedValues);
  };

  return (
    <div className="p-6 pb-20">
      {/* Header */}
      <ModuleFormHeader
        icon={<Palette className="h-5 w-5 text-primary" />}
        title={t('appearance.title', 'Appearance Settings')}
        backButtonText={t('common.back', 'Back')}
        onBack={() => navigate('/settings')}
        actions={[
          {
            icon: <RotateCcw className="h-4 w-4" />,
            tooltip: t('appearance.resetToDefaults', 'Reset to Defaults'),
            onClick: onReset
          }
        ]}
      />

      {/* Tabs */}
      <div className="mt-8">
        <Tabs defaultValue="theme" className="space-y-2">
          <TabsList className="w-full justify-start bg-card border border-border rounded-lg p-1 overflow-x-auto flex-nowrap">
            <TabsTrigger
              value="theme"
              icon={<Monitor className="w-4 h-4" />}
            >
              {t('appearance.tabs.theme', 'Theme')}
            </TabsTrigger>

            <TabsTrigger
              value="colors"
              icon={<Palette className="w-4 h-4" />}
            >
              {t('appearance.tabs.colors', 'Colors')}
            </TabsTrigger>

            <TabsTrigger
              value="tooltips"
              icon={<MessageSquare className="w-4 h-4" />}
            >
              {t('appearance.tabs.tooltips', 'Tooltips')}
            </TabsTrigger>

            <TabsTrigger
              value="notifications"
              icon={<Bell className="w-4 h-4" />}
            >
              {t('appearance.tabs.notifications', 'Notifications')}
            </TabsTrigger>

            <TabsTrigger
              value="dashboard"
              icon={<LayoutGrid className="w-4 h-4" />}
            >
              {t('appearance.tabs.dashboard', 'Dashboard')}
            </TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Tab: Theme */}
            <TabsContent value="theme" className="space-y-3 mt-2">
              <ThemeTab watchedValues={watchedValues} register={register} />
            </TabsContent>

            {/* Tab: Colors */}
            <TabsContent value="colors" className="space-y-3 mt-2">
              <ColorsTab
                watchedValues={watchedValues}
                register={register}
                setValue={setValue}
                applyColorPreset={applyColorPreset}
              />
            </TabsContent>

            {/* Tab: Tooltips */}
            <TabsContent value="tooltips" className="space-y-3 mt-2">
              <TooltipsTab
                watchedValues={watchedValues}
                register={register}
                setValue={setValue}
              />
            </TabsContent>

            {/* Tab: Notifications */}
            <TabsContent value="notifications" className="space-y-3 mt-2">
              <NotificationsTab
                watchedValues={watchedValues}
                register={register}
                setValue={setValue}
              />
            </TabsContent>

            {/* Tab: Dashboard */}
            <TabsContent value="dashboard" className="space-y-3 mt-2">
              <DashboardTab
                watchedValues={watchedValues}
                register={register}
                setValue={setValue}
              />
            </TabsContent>
          </form>
        </Tabs>
      </div>

      {/* Fixed Action Bar */}
      <FixedActionBar
        onSave={() => handleSubmit(onSubmit)()}
        onCancel={onReset}
        saveDisabled={isLoading}
        isLoading={isLoading}
        saveText={t('common.save', 'Save')}
        cancelText={t('common.cancel', 'Cancel')}
      />
    </div>
  );
}

